/**
 * StateCapture.js
 * Utility for serializing and deserializing complete scene state
 * Captures: objects, groups, materials, transforms, selection, counters
 */

import { SHAPE_TYPES } from '../utils/Constants.js';

class StateCapture {
    /**
     * Capture the complete current state of the scene
     * @param {THREE.Scene} scene - The Three.js scene
     * @param {SelectionManager} selectionManager - Selection manager instance
     * @param {GroupManager} groupManager - Group manager instance
     * @param {ShapeFactory} shapeFactory - Shape factory instance
     * @returns {Object} Serialized state object
     */
    static captureState(scene, selectionManager, groupManager, shapeFactory) {
        const state = {
            timestamp: Date.now(),
            objects: [],
            groups: [],
            selection: [],
            counters: {}
        };

        // Capture shape counters from ShapeFactory
        if (shapeFactory && shapeFactory.shapeCounters) {
            state.counters = { ...shapeFactory.shapeCounters };
        }

        // Get all user objects and group containers (exclude grid, lights, etc)
        const userObjects = scene.children.filter(obj => 
            obj.userData && (obj.userData.isUserObject || obj.userData.isGroupResult)
        );

        // Capture objects and groups
        userObjects.forEach(obj => {
            if (groupManager && groupManager.isGroupContainer(obj)) {
                // This is a group container
                state.groups.push(this.serializeGroup(obj, groupManager));
            } else if (!groupManager || !groupManager.isGrouped(obj)) {
                // This is a standalone object (not inside a group)
                state.objects.push(this.serializeObject(obj));
            }
        });

        // Capture current selection
        const selectedObjects = selectionManager.getSelectedObjects();
        state.selection = selectedObjects.map(obj => obj.uuid);

        return state;
    }

    /**
     * Serialize a single object
     * @param {THREE.Object3D} obj - The object to serialize
     * @returns {Object} Serialized object data
     */
    static serializeObject(obj) {
        const data = {
            uuid: obj.uuid,
            name: obj.name,
            type: obj.userData.shapeType || 'unknown',
            position: obj.position.toArray(),
            rotation: obj.rotation.toArray(),
            scale: obj.scale.toArray(),
            visible: obj.visible,
            userData: { ...obj.userData }
        };

        // Serialize material
        if (obj.material) {
            data.material = {
                color: obj.material.color.getHex(),
                opacity: obj.material.opacity,
                transparent: obj.material.transparent,
                wireframe: obj.material.wireframe
            };
        }

        // Special handling for text objects
        if (data.type === SHAPE_TYPES.TEXT && obj.userData.textContent) {
            data.textContent = obj.userData.textContent;
            data.textFont = obj.userData.textFont || 'sans';
        }

        return data;
    }

    /**
     * Serialize a group container with its children
     * @param {THREE.Group} group - The group to serialize
     * @param {GroupManager} groupManager - Group manager instance
     * @returns {Object} Serialized group data
     */
    static serializeGroup(group, groupManager) {
        const groupRecord = groupManager.getGroupByContainer(group);
        
        const data = {
            uuid: group.uuid,
            id: groupRecord ? groupRecord.id : null,
            name: group.name,
            position: group.position.toArray(),
            rotation: group.rotation.toArray(),
            scale: group.scale.toArray(),
            visible: group.visible,
            children: []
        };

        // Serialize all children in the group
        const children = groupManager.getGroupChildren(group);
        children.forEach(child => {
            data.children.push(this.serializeObject(child));
        });

        return data;
    }

    /**
     * Restore state to the scene
     * @param {Object} state - The state object to restore
     * @param {THREE.Scene} scene - The Three.js scene
     * @param {SelectionManager} selectionManager - Selection manager instance
     * @param {GroupManager} groupManager - Group manager instance
     * @param {ShapeFactory} shapeFactory - Shape factory instance
     * @param {TransformManager} transformManager - Transform manager instance (optional)
     * @returns {Array} Array of restored objects
     */
    static restoreState(state, scene, selectionManager, groupManager, shapeFactory, transformManager = null) {
        const restoredObjects = [];
        const uuidMap = new Map(); // Map old UUIDs to new objects

        // Detach transform controls before clearing scene to avoid errors
        if (transformManager) {
            transformManager.detach();
        }

        // Clear current scene (remove all user objects and group containers),
        // disposing every owned resource in each removed subtree (F16)
        const userObjects = scene.children.filter(obj => 
            obj.userData && (obj.userData.isUserObject || obj.userData.isGroupResult)
        );
        userObjects.forEach(obj => {
            scene.remove(obj);
            obj.traverse && obj.traverse(child => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(m => m.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
        });

        // Clear GroupManager's internal groups array
        if (groupManager && groupManager.groups) {
            groupManager.groups = [];
            groupManager.groupIdCounter = 0;
        }

        // Clear selection
        selectionManager.clear();

        // Restore shape counters
        if (state.counters && shapeFactory) {
            shapeFactory.shapeCounters = { ...state.counters };
        }

        // Restore standalone objects
        state.objects.forEach(objData => {
            const obj = this.deserializeObject(objData, shapeFactory);
            if (obj) {
                scene.add(obj);
                restoredObjects.push(obj);
                uuidMap.set(objData.uuid, obj);
            }
        });

        // Restore groups
        state.groups.forEach(groupData => {
            const { children } = this.deserializeGroup(groupData, shapeFactory);
            if (children.length > 0) {
                // Restore the group record directly (no re-centering) so saved
                // child-local transforms stay valid (F08)
                const group = groupManager.restoreGroup({
                    id: groupData.id || `group_${groupManager.groupIdCounter++}`,
                    name: groupData.name,
                    transform: {
                        position: groupData.position,
                        rotation: [0, 0, 0, 1],
                        scale: groupData.scale
                    }
                });
                
                const container = group.container;
                container.rotation.fromArray(groupData.rotation);
                container.visible = groupData.visible;
                container.uuid = groupData.uuid; // Preserve UUID for selection
                
                children.forEach((child, index) => {
                    const childData = groupData.children[index];
                    // Restore the saved LOCAL transform beneath the container
                    if (childData) {
                        child.position.fromArray(childData.position);
                        child.rotation.fromArray(childData.rotation);
                        child.scale.fromArray(childData.scale);
                        uuidMap.set(childData.uuid, child);
                    }
                    groupManager.restoreObjectToGroup(child, group.id);
                    restoredObjects.push(child);
                });
                
                restoredObjects.push(container);
                uuidMap.set(groupData.uuid, container);
            }
        });

        // Restore selection
        if (state.selection && state.selection.length > 0) {
            const selectedObjects = [];
            state.selection.forEach(uuid => {
                const obj = uuidMap.get(uuid);
                if (obj) {
                    selectedObjects.push(obj);
                }
            });
            
            // Apply selection
            if (selectedObjects.length > 0) {
                selectionManager.select(selectedObjects[0]);
                for (let i = 1; i < selectedObjects.length; i++) {
                    selectionManager.addToSelection(selectedObjects[i]);
                }
                
                // Re-attach transform controls to first selected (or group container if applicable)
                if (transformManager) {
                    const primarySelection = selectedObjects.find(obj => 
                        groupManager.isGroupContainer(obj)
                    ) || selectedObjects[0];
                    
                    if (selectedObjects.length === 1 || groupManager.isGroupContainer(primarySelection)) {
                        transformManager.attach(primarySelection);
                    }
                }
            }
        }

        return restoredObjects;
    }

    /**
     * Deserialize an object from data
     * @param {Object} data - Serialized object data
     * @param {ShapeFactory} shapeFactory - Shape factory instance
     * @returns {THREE.Object3D|null} Restored object
     */
    static deserializeObject(data, shapeFactory) {
        if (!shapeFactory) return null;

        const options = {
            color: data.material?.color,
            position: data.position,
            rotation: data.rotation.slice(0, 3), // Exclude rotation order
            scale: data.scale
        };

        // Special handling for text - restore ALL geometry parameters (F08)
        if (data.type === SHAPE_TYPES.TEXT) {
            options.text = data.textContent || 'Text';
            options.font = data.textFont || 'sans';
            options.height = data.userData?.textHeight || 0.2;
            options.bevel = data.userData?.textBevel || 0.01;
        }

        // Restore box/sphere dimension options (F17 roundtrip)
        if (data.type === SHAPE_TYPES.BOX && data.userData?.boxSize) {
            options.width = data.userData.boxSize[0];
            options.height = data.userData.boxSize[1];
            options.depth = data.userData.boxSize[2];
        }
        if (data.type === SHAPE_TYPES.SPHERE && data.userData?.sphereRadius != null) {
            options.radius = data.userData.sphereRadius;
        }

        // Special handling for polygon - ensure sides are applied when recreating
        if (data.type === SHAPE_TYPES.POLYGON) {
            // sides may be stored at top-level or in userData
            let sides = data.userData?.polygonSides || data.polygonSides || 5;
            // clamp sides between 3 and 12
            sides = Math.max(3, Math.min(12, parseInt(sides, 10) || 5));
            options.sides = sides;
        }

        // Special handling for tube
        if (data.type === SHAPE_TYPES.TUBE) {
            options.radius = data.userData?.outerRadius || 0.5;
            options.holeRadius = data.userData?.holeRadius || 0.25;
            options.length = data.userData?.length || 1;
        }

        // Create the object
        const obj = shapeFactory.createShape(data.type, options);
        if (!obj) return null;

        // Restore properties
        obj.uuid = data.uuid; // Preserve UUID for proper selection restoration
        obj.name = data.name;
        obj.visible = data.visible;
        obj.userData = { ...data.userData };

        // Restore material properties
        if (data.material && obj.material) {
            // Restore the exact serialized color, including black (F06/F08)
            if (Number.isFinite(data.material.color)) {
                obj.material.color.setHex(data.material.color);
            }
            obj.material.opacity = data.material.opacity;
            obj.material.transparent = data.material.transparent;
            obj.material.wireframe = data.material.wireframe;
        }

        return obj;
    }

    /**
     * Deserialize a group from data
     * @param {Object} data - Serialized group data
     * @param {ShapeFactory} shapeFactory - Shape factory instance
     * @returns {Object} Object with group container and children array
     */
    static deserializeGroup(data, shapeFactory) {
        const children = [];

        // Deserialize all children
        data.children.forEach(childData => {
            const child = this.deserializeObject(childData, shapeFactory);
            if (child) {
                children.push(child);
            }
        });

        return {
            group: data,
            children: children
        };
    }

    /**
     * Create a deep clone of a state object
     * @param {Object} state - State to clone
     * @returns {Object} Cloned state
     */
    static cloneState(state) {
        return JSON.parse(JSON.stringify(state));
    }

    /**
     * Remove volatile metadata (timestamps) so comparisons reflect semantic
     * state only (F21)
     */
    static stripVolatile(state) {
        if (!state || typeof state !== 'object') return state;
        const { timestamp, ...rest } = state;
        void timestamp;
        return rest;
    }

    /**
     * Compare two states for equality (useful for detecting changes).
     * Volatile metadata such as capture timestamps is excluded (F21)
     * @param {Object} stateA - First state
     * @param {Object} stateB - Second state
     * @returns {boolean} True if states are equal
     */
    static areStatesEqual(stateA, stateB) {
        return JSON.stringify(this.stripVolatile(stateA)) === JSON.stringify(this.stripVolatile(stateB));
    }
}

export default StateCapture;
